import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";

const ASSESSMENT_GENERATION_POLL_INTERVAL_MS = 1_500;
const RECOGNIZED_ASSESSMENT_GENERATION_STATUSES = [
  "RUNNING",
  "COMPLETED",
  "FAILED",
] as const;
export type AssessmentGenerationStatus =
  (typeof RECOGNIZED_ASSESSMENT_GENERATION_STATUSES)[number];

export const isRecognizedAssessmentGenerationStatus = (
  status?: string,
): status is AssessmentGenerationStatus =>
  RECOGNIZED_ASSESSMENT_GENERATION_STATUSES.some(
    (recognizedStatus) => recognizedStatus === status,
  );

export interface AssessmentGenerationStatusResponse {
  workflowId?: string;
  status?: string;
  startedAt?: string;
  btSpanId?: string;
  flagged: boolean;
  higherReasoningRequested?: boolean;
}

export const useAssessmentGenerationStatus = ({
  encounterId,
  expectedWorkflowId,
  patientId,
  enabled = true,
}: {
  encounterId?: string;
  expectedWorkflowId?: string;
  patientId?: string;
  enabled?: boolean;
}) => {
  return useApi<AssessmentGenerationStatusResponse>({
    queryOptions: {
      enabled: Boolean(encounterId && patientId) && enabled,
      queryKey:
        QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.GENERATION_STATUS(
          patientId,
          encounterId,
        ),
      refetchInterval: (query) => {
        const generationStatusResponse = query.state.data;
        const generationStatus = generationStatusResponse?.status;
        const isAwaitingExpectedWorkflow =
          expectedWorkflowId != null &&
          (generationStatusResponse?.workflowId !== expectedWorkflowId ||
            !isRecognizedAssessmentGenerationStatus(generationStatus));

        if (isAwaitingExpectedWorkflow || generationStatus === "RUNNING") {
          return ASSESSMENT_GENERATION_POLL_INTERVAL_MS;
        }

        return false;
      },
      queryFn: async () => {
        if (expectedWorkflowId) {
          const response =
            await axiosInstance.get<AssessmentGenerationStatusResponse>(
              `/charting/ai-charting/assessment/generation-status/${encodeURIComponent(expectedWorkflowId)}`,
            );

          return response.data;
        }

        const response =
          await axiosInstance.get<AssessmentGenerationStatusResponse>(
            "/charting/ai-charting/assessment/generation-status",
            {
              params: {
                encounterId,
                patientId,
              },
            },
          );

        return response.data;
      },
    },
  });
};
